'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import {
  ShoppingBag,
  Search,
  Heart,
  ArrowLeftRight,
  User,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Phone,
  Layers,
  ExternalLink,
  Sparkles,
  Gift,
  Package,
  Plane,
  Truck,
  ArrowRight,
  ShieldCheck,
  Flame,
  Leaf,
} from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';

export interface MegaMenuTabItem {
  name: string;
  image: string;
  href: string;
  badge?: string;
  price?: number;
  subtitle?: string;
}

export interface MegaMenuTab {
  id: string;
  title: string;
  description?: string;
  viewAllHref: string;
  items: MegaMenuTabItem[];
}



interface HeaderProps {
  initialHide?: boolean;
}

export default function Header({ initialHide }: HeaderProps = {}) {
  const pathname = usePathname();
  const {
    cartCount,
    cartSubtotal,
    setIsCartOpen,
    setIsRfqOpen,
    setIsTrackingOpen,
    setIsAccountOpen,
    setIsContactOpen,
    products,
    categories,
    hampers,
    refreshCatalog,
  } = useCommerce();

  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [activeMegaCategory, setActiveMegaCategory] = useState(0);
  const [bulkExportOpen, setBulkExportOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [wishlistCount] = useState(2);
  const [isScrolled, setIsScrolled] = useState(false);

  // UNERO layout selectors & states
  const [selectedLang, setSelectedLang] = useState('Eng');
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const langRef = useRef<HTMLDivElement>(null);
  const currencyRef = useRef<HTMLDivElement>(null);
  const announcementRef = useRef<HTMLDivElement>(null);
  const headerNavRef = useRef<HTMLElement>(null);
  const [announcementHeight, setAnnouncementHeight] = useState<number | null>(null);
  const [navHeight, setNavHeight] = useState<number | null>(null);
  const [loggedInUser, setLoggedInUser] = useState<any>(null);

  useEffect(() => {
    try {
      const u = localStorage.getItem('elavarasi_customer_user');
      if (u) setLoggedInUser(JSON.parse(u));
    } catch {}

    const handleStorageChange = () => {
      try {
        const u = localStorage.getItem('elavarasi_customer_user');
        setLoggedInUser(u ? JSON.parse(u) : null);
      } catch {}
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangDropdownOpen(false);
      }
      if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
        setCurrencyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const updateHeights = () => {
      if (announcementRef.current) {
        setAnnouncementHeight(announcementRef.current.offsetHeight);
      }
      if (headerNavRef.current) {
        setNavHeight(headerNavRef.current.offsetHeight);
      }
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateHeights);
      if (announcementRef.current) ro.observe(announcementRef.current);
      if (headerNavRef.current) ro.observe(headerNavRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateHeights);
      ro?.disconnect();
    };
  }, [showAnnouncement, isSearchOpen]);

  const isHome = pathname === '/';
  const shouldHideInitially = initialHide !== undefined ? initialHide : false;
  const isVisible = !shouldHideInitially || isScrolled || mobileMenuOpen || megaMenuOpen || bulkExportOpen || helpOpen;

  const currentAnnouncementHeight = showAnnouncement
    ? (announcementHeight !== null ? announcementHeight : 27)
    : 0;

  const currentNavHeight = navHeight !== null ? navHeight : 92;

  const spacerHeight = shouldHideInitially
    ? currentAnnouncementHeight
    : currentAnnouncementHeight + currentNavHeight;

  const megaMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bulkExportTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const helpTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const carouselTrackRef = useRef<HTMLDivElement>(null);

  const lastCatalogRefreshRef = useRef<number>(0);

  const handleMegaMenuEnter = () => {
    if (megaMenuTimeoutRef.current) clearTimeout(megaMenuTimeoutRef.current);
    if (bulkExportTimeoutRef.current) clearTimeout(bulkExportTimeoutRef.current);
    if (helpTimeoutRef.current) clearTimeout(helpTimeoutRef.current);
    setBulkExportOpen(false);
    setHelpOpen(false);
    setMegaMenuOpen(true);

    const now = Date.now();
    if (now - lastCatalogRefreshRef.current > 30000) {
      lastCatalogRefreshRef.current = now;
      refreshCatalog?.().catch(() => {});
    }
  };

  const handleMegaMenuLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(false);
    }, 180);
  };

  const handleBulkExportEnter = () => {
    if (bulkExportTimeoutRef.current) clearTimeout(bulkExportTimeoutRef.current);
    if (megaMenuTimeoutRef.current) clearTimeout(megaMenuTimeoutRef.current);
    if (helpTimeoutRef.current) clearTimeout(helpTimeoutRef.current);
    setMegaMenuOpen(false);
    setHelpOpen(false);
    setBulkExportOpen(true);
  };

  const handleBulkExportLeave = () => {
    bulkExportTimeoutRef.current = setTimeout(() => {
      setBulkExportOpen(false);
    }, 180);
  };

  const handleHelpEnter = () => {
    if (helpTimeoutRef.current) clearTimeout(helpTimeoutRef.current);
    if (megaMenuTimeoutRef.current) clearTimeout(megaMenuTimeoutRef.current);
    if (bulkExportTimeoutRef.current) clearTimeout(bulkExportTimeoutRef.current);
    setMegaMenuOpen(false);
    setBulkExportOpen(false);
    setHelpOpen(true);
  };

  const handleHelpLeave = () => {
    helpTimeoutRef.current = setTimeout(() => {
      setHelpOpen(false);
    }, 180);
  };

  const scrollMegaCarousel = (direction: 'left' | 'right') => {
    if (carouselTrackRef.current) {
      const scrollAmount = 220;
      carouselTrackRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    setMegaMenuOpen(false);
    setBulkExportOpen(false);
    setHelpOpen(false);
    setMobileMenuOpen(false);
    setLangDropdownOpen(false);
    setCurrencyDropdownOpen(false);
    setIsSearchOpen(false);
  }, [pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setIsSearchOpen(false);
    const target = document.getElementById('deal-of-the-day') || document.getElementById('categories') || document.getElementById('products');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isActive = (path: string) => {
    if (path === '/' && pathname === '/') return true;
    if (path !== '/' && pathname?.startsWith(path)) return true;
    return false;
  };

  // Dynamically compute mega menu tabs from live database categories & products
  const megaMenuTabs = useMemo<MegaMenuTab[]>(() => {
    if (categories && categories.length > 0) {
      const orderMap: Record<string, number> = {
        'chips-crisps': 1,
        'banana-chips': 1,
        'chips': 1,
        'kai-murukku': 2,
        'murukku-savouries': 2,
        'murukku': 2,
        'savouries-mixture': 3,
        'mixture-karas': 3,
        'savouries': 3,
        'sweets-halwa': 4,
        'traditional-sweets': 4,
        'sweets': 4,
        'gift-hampers-boxes': 5,
        'festival-hampers': 5,
        'hampers': 5,
        'bulk-corporate': 6,
        'export-packs': 6,
        'bulk': 6,
      };

      const sortedCategories = [...categories].sort((a, b) => {
        const aKey = (a.slug || a.id || '').toLowerCase();
        const bKey = (b.slug || b.id || '').toLowerCase();
        const aOrder = orderMap[aKey] || 99;
        const bOrder = orderMap[bKey] || 99;
        return aOrder - bOrder;
      });

      const dynamicTabs: MegaMenuTab[] = sortedCategories.map((cat) => {
        const catKey = (cat.slug || cat.id || '').toLowerCase();
        const catIdStr = String(cat.id || (cat as any)._id || '');

        // 1. Hampers category -> show database hampers or products
        if (catKey.includes('hamper') || catKey.includes('gift')) {
          const hamperItems: MegaMenuTabItem[] = (hampers && hampers.length > 0)
            ? hampers.slice(0, 8).map(h => ({
                name: h.title,
                image: h.image || '/images/royal_gift_box.jpg',
                href: '/occasions',
                badge: h.badge || 'Gift Hamper',
                price: h.basePrice,
                subtitle: `MOQ: ${h.minOrderQty} packs`
              }))
            : products
                .filter(p => {
                  const pCat = (p.category || '').toLowerCase();
                  const pMain = String((p as any).mainCategory?._id || (p as any).mainCategory || '');
                  return pCat.includes('hamper') || pCat.includes('gift') || pMain === catIdStr;
                })
                .slice(0, 8)
                .map(p => ({
                  name: p.name,
                  image: p.image || '/images/royal_gift_box.jpg',
                  href: `/product/${p.slug}`,
                  badge: p.isBestseller ? 'Bestseller' : 'Gift Box',
                  price: p.variants?.[0]?.price,
                  subtitle: p.tamilName || (p.variants?.[0]?.price ? `From ₹${p.variants[0].price}` : undefined)
                }));

          if (hamperItems.length === 0) {
            hamperItems.push({
              name: `Explore ${cat.name}`,
              image: cat.image || '/images/royal_gift_box.jpg',
              href: '/occasions',
              badge: 'Hampers',
              subtitle: 'Festive & Occasion Gifting'
            });
          }

          return {
            id: cat.id,
            title: cat.name,
            description: cat.description || 'Artisanal gift hampers and celebration boxes',
            viewAllHref: '/occasions',
            items: hamperItems,
          };
        }

        // 2. Bulk & Corporate category
        if (catKey.includes('bulk') || catKey.includes('corporate') || catKey.includes('export')) {
          const bulkProducts = products.filter(p => {
            const pCat = (p.category || '').toLowerCase();
            const pMain = String((p as any).mainCategory?._id || (p as any).mainCategory || '');
            const pSlug = (p.slug || '').toLowerCase();
            return pCat.includes('bulk') || pCat.includes('corporate') || pCat.includes('export') || pMain === catIdStr || pSlug.includes('bulk') || pSlug.includes('export') || pSlug.includes('custom-box');
          });

          const bulkItems: MegaMenuTabItem[] = bulkProducts.length > 0
            ? bulkProducts.slice(0, 8).map(p => ({
                name: p.name,
                image: p.image || '/images/custom_box_carton.jpg',
                href: `/product/${p.slug}`,
                badge: p.isBestseller ? 'Bestseller' : 'Commercial',
                price: p.variants?.[0]?.price,
                subtitle: p.tamilName || (p.variants?.[0]?.weight ? `From ₹${p.variants[0].price} (${p.variants[0].weight})` : undefined),
              }))
            : [
                {
                  name: `Explore ${cat.name}`,
                  image: cat.image || '/images/custom_box_carton.jpg',
                  href: '/bulk-orders',
                  badge: 'Wholesale',
                  subtitle: 'Bulk & Corporate Inquiries'
                }
              ];

          return {
            id: cat.id,
            title: cat.name,
            description: cat.description || 'Custom corporate gifting chests and wholesale cartons',
            viewAllHref: '/bulk-orders',
            items: bulkItems,
          };
        }

        // 3. Match food products from database
        const matchingProducts = products.filter(p => {
          const pCat = (p.category || '').toLowerCase();
          const pMain = String((p as any).mainCategory?._id || (p as any).mainCategory || '');
          const pSlug = (p.slug || '').toLowerCase();

          if (pMain && (pMain === catIdStr || pMain === String((cat as any)._id))) return true;
          if (pCat === catKey) return true;

          if (catKey.includes('chips') && (pCat.includes('chips') || pSlug.includes('chips') || pSlug.includes('wafers') || pSlug.includes('upperi'))) return true;
          if (catKey.includes('murukku') && (pCat.includes('murukku') || pSlug.includes('murukku') || pSlug.includes('thattai') || pSlug.includes('pakoda'))) return true;
          if (catKey.includes('mixture') && (pCat.includes('mixture') || pCat.includes('savouries') || pSlug.includes('mixture') || pSlug.includes('pakoda') || pSlug.includes('thattai') || pSlug.includes('tapioca'))) return true;
          if (catKey.includes('sweet') && (pCat.includes('sweet') || pCat.includes('halwa') || pSlug.includes('mysore') || pSlug.includes('halwa') || pSlug.includes('jaggery') || pSlug.includes('upperi'))) return true;
          return false;
        });

        const items: MegaMenuTabItem[] = matchingProducts.slice(0, 8).map(p => ({
          name: p.name,
          image: p.image || '/images/pouch_kai_murukku.png',
          href: `/product/${p.slug}`,
          badge: p.isBestseller ? 'Bestseller' : p.isFestiveSpecial ? 'Festive' : undefined,
          price: p.variants?.[0]?.price,
          subtitle: p.tamilName || (p.variants?.[0]?.weight ? `From ₹${p.variants[0].price} (${p.variants[0].weight})` : undefined),
        }));

        if (items.length === 0) {
          items.push({
            name: `Explore ${cat.name}`,
            image: cat.image || '/images/pouch_kai_murukku.png',
            href: `/shop?category=${cat.slug || cat.id}`,
            badge: 'Collection',
            subtitle: 'Fresh batch frying daily'
          });
        }

        return {
          id: cat.id,
          title: cat.name,
          description: cat.description || `Authentic ${cat.name} fried in traditional wood-fired vats`,
          viewAllHref: `/shop?category=${cat.slug || cat.id}`,
          items,
        };
      });

      return dynamicTabs;
    }

    return [];
  }, [categories, products, hampers]);

  const safeMegaIndex = Math.min(activeMegaCategory, Math.max(0, megaMenuTabs.length - 1));
  const currentTab = megaMenuTabs.length > 0 ? (megaMenuTabs[safeMegaIndex] || megaMenuTabs[0]) : null;

  return (
    <>
      {/* FIXED HEADER WRAPPER (Keeps announcement bar always at the top, navbar reveals right below it) */}
      <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none flex flex-col">
        {/* 1. TOP ANNOUNCEMENT BAR (Always visible at the top of viewport, even while scrolling) */}
        {showAnnouncement && (
          <div 
            ref={announcementRef}
            className="pointer-events-auto relative z-20 w-full bg-[#1C2E23] text-[#F7F4D6] px-4 sm:px-6 py-1 text-[10px] sm:text-[11px] tracking-wider border-b border-[#E09F55]/25 shadow-xs"
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              {/* Left spacer for symmetrical optical balance */}
              <div className="w-6 hidden md:block" />

              {/* Announcement Message */}
              <div className="flex-1 text-center font-medium flex items-center justify-center flex-wrap gap-x-2 gap-y-0.5">
                <span className="text-[#F2BC6F]">✈</span>
                <span className="font-semibold uppercase tracking-wider text-[10px] sm:text-[11px]">
                  SHOP TIME MON – SAT | 8:30 AM – 9:00 PM IST
                </span>
                <span className="text-[#DDDAC4]/50 hidden sm:inline">•</span>
                <a
                  href="https://wa.me/919443210890"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1 text-[#F2BC6F] hover:text-white transition-colors font-bold"
                >
                  <span>📞 +91 94432 10890 (WHATSAPP)</span>
                </a>
                <span className="text-[#DDDAC4]/50 hidden md:inline">•</span>
                <span className="hidden md:inline text-[#DDDAC4]">
                  FREE EXPRESS DISPATCH OVER ₹499
                </span>
                <span className="text-[#F2BC6F]">✈</span>
              </div>

              {/* Close Button on Right */}
              <button
                onClick={() => setShowAnnouncement(false)}
                className="text-[#DDDAC4]/70 hover:text-white p-0.5 rounded transition-colors cursor-pointer shrink-0 ml-2"
                aria-label="Close announcement banner"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        {/* 2. MAIN NAVBAR (UNERO 2-Tier Luxury Layout: Row 1 Selectors + Logo + Icons | Row 2 Centered Navigation) */}
        <header 
          ref={headerNavRef}
          className={`pointer-events-auto relative z-10 w-full bg-white text-neutral-900 transition-all duration-300 ease-in-out border-b border-neutral-200/80 shadow-xs ${
            isVisible 
              ? 'translate-y-0 opacity-100' 
              : '-translate-y-full opacity-0 pointer-events-none'
          }`}
        >
          {/* TIER 1: Top Bar (Left: Language & Currency | Center: Logo | Right: Search, Account, Cart) */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-3.5 flex items-center justify-between relative min-h-[56px]">
            
            {/* LEFT: Language & Currency Selectors (Desktop) & Hamburger (Mobile) */}
            <div className="flex items-center space-x-3 sm:space-x-4 shrink-0 z-10">
              {/* Mobile Hamburger on Left */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-1.5 -ml-1.5 rounded-md text-neutral-800 hover:text-black hover:bg-neutral-100 transition-colors cursor-pointer"
                aria-label="Toggle Navigation Menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5 text-black" /> : <Menu className="w-5 h-5" />}
              </button>

              {/* Desktop Language Selector */}
              <div className="hidden lg:block relative" ref={langRef}>
                <button
                  type="button"
                  onClick={() => {
                    setLangDropdownOpen(!langDropdownOpen);
                    setCurrencyDropdownOpen(false);
                  }}
                  className="flex items-center space-x-1 text-xs font-medium text-neutral-600 hover:text-black transition-colors cursor-pointer py-1"
                >
                  <span>{selectedLang}</span>
                  <ChevronDown className={`w-3 h-3 text-neutral-400 transition-transform ${langDropdownOpen ? 'rotate-180 text-black' : ''}`} />
                </button>
                {langDropdownOpen && (
                  <div className="absolute left-0 top-full mt-2 w-32 bg-white border border-neutral-200 shadow-xl rounded-md py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                    <button
                      type="button"
                      onClick={() => { setSelectedLang('Eng'); setLangDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-neutral-50 transition-colors ${selectedLang === 'Eng' ? 'font-bold text-black bg-neutral-50' : 'text-neutral-700'}`}
                    >
                      <span>English</span>
                      {selectedLang === 'Eng' && <span className="text-[10px] text-[#6F0A06]">✓</span>}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSelectedLang('தமிழ்'); setLangDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-neutral-50 transition-colors ${selectedLang === 'தமிழ்' ? 'font-bold text-black bg-neutral-50' : 'text-neutral-700'}`}
                    >
                      <span>தமிழ்</span>
                      {selectedLang === 'தமிழ்' && <span className="text-[10px] text-[#6F0A06]">✓</span>}
                    </button>
                  </div>
                )}
              </div>

              {/* Desktop Currency Selector */}
              <div className="hidden lg:block relative" ref={currencyRef}>
                <button
                  type="button"
                  onClick={() => {
                    setCurrencyDropdownOpen(!currencyDropdownOpen);
                    setLangDropdownOpen(false);
                  }}
                  className="flex items-center space-x-1 text-xs font-medium text-neutral-600 hover:text-black transition-colors cursor-pointer py-1"
                >
                  <span>{selectedCurrency}</span>
                  <ChevronDown className={`w-3 h-3 text-neutral-400 transition-transform ${currencyDropdownOpen ? 'rotate-180 text-black' : ''}`} />
                </button>
                {currencyDropdownOpen && (
                  <div className="absolute left-0 top-full mt-2 w-36 bg-white border border-neutral-200 shadow-xl rounded-md py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                    {[
                      { code: 'INR', label: 'INR (₹)' },
                      { code: 'USD', label: 'USD ($)' },
                      { code: 'EUR', label: 'EUR (€)' },
                      { code: 'AED', label: 'AED (د.إ)' },
                      { code: 'SGD', label: 'SGD (S$)' },
                    ].map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => { setSelectedCurrency(c.code); setCurrencyDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-neutral-50 transition-colors ${selectedCurrency === c.code ? 'font-bold text-black bg-neutral-50' : 'text-neutral-700'}`}
                      >
                        <span>{c.label}</span>
                        {selectedCurrency === c.code && <span className="text-[10px] text-[#6F0A06]">✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* CENTER: Prominent Centered Brand Logo */}
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
              <Link href="/" className="flex items-center space-x-2.5 group py-0.5" title="Elavarasi Foods Home">
                <Image
                  src="/logo-web.png"
                  alt="Elavarasi Foods"
                  width={38}
                  height={38}
                  priority
                  unoptimized
                  className="h-7 sm:h-8 md:h-9 w-auto object-contain transition-transform duration-200 group-hover:scale-105"
                />
                <span className="font-serif tracking-[0.24em] text-lg sm:text-xl md:text-2xl font-black text-neutral-900 group-hover:text-[#6F0A06] transition-colors uppercase select-none">
                  ELAVARASI
                </span>
              </Link>
            </div>

            {/* RIGHT: Search, Account, Shopping Bag with Red Count Badge */}
            <div className="flex items-center space-x-3.5 sm:space-x-5 text-neutral-800 shrink-0 z-10">
              {/* Search Toggle Icon */}
              <button
                type="button"
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-1 hover:text-black transition-colors cursor-pointer"
                aria-label="Search"
                title="Search products"
              >
                <Search className="w-4 h-4 sm:w-5 sm:h-5 stroke-[1.8]" />
              </button>

              {/* User Account Icon */}
              <Link
                href="/profile"
                className="p-1 hover:text-black transition-colors cursor-pointer flex items-center gap-1.5"
                aria-label="My Account"
                title={loggedInUser ? `Account (${loggedInUser.name})` : "My Account"}
              >
                {loggedInUser ? (
                  <span className="w-6 h-6 rounded-full bg-[#6F0A06] text-[#F7F4D6] text-[10px] font-bold flex items-center justify-center border border-[#E09F55]/60 shadow-xs">
                    {loggedInUser.name?.slice(0, 1).toUpperCase() || 'U'}
                  </span>
                ) : (
                  <User className="w-4 h-4 sm:w-5 sm:h-5 stroke-[1.8]" />
                )}
              </Link>

              {/* Shopping Bag Icon with Round Red Counter Badge */}
              <button
                type="button"
                onClick={() => setIsCartOpen(true)}
                className="p-1 hover:text-black transition-colors relative cursor-pointer"
                aria-label="Shopping Bag"
                title={`Shopping Bag (${cartCount} items)`}
              >
                <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 stroke-[1.8]" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-[#E53935] text-white text-[10px] font-bold flex items-center justify-center shadow-xs">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Expandable Search Drawer */}
          {isSearchOpen && (
            <div className="w-full bg-neutral-50 border-t border-neutral-200 px-4 py-3 animate-in fade-in slide-in-from-top-1 duration-200">
              <div className="max-w-2xl mx-auto flex items-center gap-3">
                <Search className="w-4 h-4 text-neutral-400 shrink-0" />
                <form onSubmit={handleSearch} className="flex-1 flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search banana chips, kai murukku, mysore pak, hampers..."
                    className="w-full bg-transparent text-neutral-900 placeholder:text-neutral-400 text-sm focus:outline-none py-1"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="ml-2 px-3 py-1 bg-neutral-900 hover:bg-[#6F0A06] text-white text-xs font-semibold rounded transition-colors cursor-pointer"
                  >
                    Search
                  </button>
                </form>
                <button
                  type="button"
                  onClick={() => setIsSearchOpen(false)}
                  className="p-1 text-neutral-400 hover:text-neutral-800 transition-colors cursor-pointer"
                  aria-label="Close search"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* TIER 2: Centered Horizontal Navigation Links */}
          <div className="w-full border-t border-neutral-100 hidden lg:flex items-center justify-center">
            <nav className="flex items-center justify-center space-x-7 xl:space-x-9 py-2.5">
              {/* HOME */}
              <Link
                href="/"
                className={`text-[13px] tracking-wide font-medium transition-colors relative py-1 ${
                  isActive('/')
                    ? 'text-neutral-900 font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-neutral-900'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Home
              </Link>

              {/* ALL COLLECTIONS / SHOP (Triggers Mega Menu) */}
              <div
                className="relative"
                onMouseEnter={handleMegaMenuEnter}
                onMouseLeave={handleMegaMenuLeave}
              >
                <Link
                  href="/shop"
                  className={`flex items-center space-x-1 text-[13px] tracking-wide font-medium transition-colors py-1 cursor-pointer ${
                    megaMenuOpen || isActive('/shop')
                      ? 'text-neutral-900 font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-neutral-900'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  <span>All Collections</span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 text-neutral-400 transition-transform duration-200 ${
                      megaMenuOpen ? 'rotate-180 text-neutral-900' : ''
                    }`}
                  />
                </Link>
              </div>

              {/* OCCASIONS */}
              <Link
                href="/occasions"
                className={`text-[13px] tracking-wide font-medium transition-colors relative py-1 ${
                  isActive('/occasions')
                    ? 'text-neutral-900 font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-neutral-900'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Occasions
              </Link>

              {/* FESTIVALS */}
              <Link
                href="/festivals"
                className={`text-[13px] tracking-wide font-medium transition-colors relative py-1 ${
                  isActive('/festivals')
                    ? 'text-neutral-900 font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-neutral-900'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Festivals
              </Link>

              {/* CUSTOM BOX */}
              <Link
                href="/custom-package"
                className={`text-[13px] tracking-wide font-medium transition-colors relative py-1 ${
                  isActive('/custom-package')
                    ? 'text-neutral-900 font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-neutral-900'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Custom Box
              </Link>

              {/* CORPORATE GIFTING */}
              <Link
                href="/corporate"
                className={`text-[13px] tracking-wide font-medium transition-colors relative py-1 ${
                  isActive('/corporate')
                    ? 'text-neutral-900 font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-neutral-900'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Corporate Gifting
              </Link>

              {/* BULK & EXPORT */}
              <div
                className="relative"
                onMouseEnter={handleBulkExportEnter}
                onMouseLeave={handleBulkExportLeave}
              >
                <button
                  type="button"
                  onClick={() => setBulkExportOpen(!bulkExportOpen)}
                  className={`flex items-center space-x-1 text-[13px] tracking-wide font-medium transition-colors py-1 cursor-pointer ${
                    bulkExportOpen || isActive('/bulk-orders') || isActive('/export')
                      ? 'text-neutral-900 font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-neutral-900'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  <span>Bulk &amp; Export</span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 text-neutral-400 transition-transform duration-200 ${
                      bulkExportOpen ? 'rotate-180 text-neutral-900' : ''
                    }`}
                  />
                </button>

                {bulkExportOpen && (
                  <div
                    className="absolute left-0 top-full pt-2 w-72 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                    onMouseEnter={handleBulkExportEnter}
                    onMouseLeave={handleBulkExportLeave}
                  >
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-2xl p-2 space-y-1 text-xs text-neutral-800">
                      <Link
                        href="/bulk-orders"
                        onClick={() => setBulkExportOpen(false)}
                        className="flex items-center space-x-2.5 px-3 py-2 rounded-md hover:bg-neutral-50 hover:text-black font-semibold transition-colors"
                      >
                        <Package className="w-4 h-4 text-[#6F0A06] shrink-0" />
                        <div>
                          <div className="font-bold text-neutral-900">Bulk Wholesale (5kg+)</div>
                          <div className="text-[10px] text-neutral-500">Tier volume rates for events</div>
                        </div>
                      </Link>
                      <Link
                        href="/export"
                        onClick={() => setBulkExportOpen(false)}
                        className="flex items-center space-x-2.5 px-3 py-2 rounded-md hover:bg-neutral-50 hover:text-black font-semibold transition-colors"
                      >
                        <Plane className="w-4 h-4 text-[#273E30] shrink-0" />
                        <div>
                          <div className="font-bold text-neutral-900">Worldwide Export Division</div>
                          <div className="text-[10px] text-neutral-500">Air freight to USA, UK, UAE, SG</div>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* ABOUT */}
              <Link
                href="/about"
                className={`text-[13px] tracking-wide font-medium transition-colors relative py-1 ${
                  isActive('/about')
                    ? 'text-neutral-900 font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-neutral-900'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                About
              </Link>

              {/* HELP & CONTACT */}
              <div
                className="relative"
                onMouseEnter={handleHelpEnter}
                onMouseLeave={handleHelpLeave}
              >
                <button
                  type="button"
                  onClick={() => setHelpOpen(!helpOpen)}
                  className={`flex items-center space-x-1 text-[13px] tracking-wide font-medium transition-colors py-1 cursor-pointer ${
                    helpOpen || isActive('/contact')
                      ? 'text-neutral-900 font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-neutral-900'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  <span>Help</span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 text-neutral-400 transition-transform duration-200 ${
                      helpOpen ? 'rotate-180 text-neutral-900' : ''
                    }`}
                  />
                </button>

                {helpOpen && (
                  <div
                    className="absolute right-0 top-full pt-2 w-64 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                    onMouseEnter={handleHelpEnter}
                    onMouseLeave={handleHelpLeave}
                  >
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-2xl p-2 space-y-1 text-xs text-neutral-800">
                      <button
                        type="button"
                        onClick={() => {
                          setHelpOpen(false);
                          setIsTrackingOpen(true);
                        }}
                        className="w-full text-left flex items-center space-x-2.5 px-3 py-2 rounded-md hover:bg-neutral-50 hover:text-black font-semibold transition-colors cursor-pointer"
                      >
                        <ArrowLeftRight className="w-4 h-4 text-neutral-700" />
                        <span>Track Order Status</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setHelpOpen(false);
                          setIsRfqOpen(true);
                        }}
                        className="w-full text-left flex items-center space-x-2.5 px-3 py-2 rounded-md hover:bg-neutral-50 hover:text-black font-semibold transition-colors cursor-pointer"
                      >
                        <Package className="w-4 h-4 text-[#E09F55]" />
                        <span>Request Official Quotation</span>
                      </button>
                      <Link
                        href="/contact"
                        onClick={() => setHelpOpen(false)}
                        className="flex items-center space-x-2.5 px-3 py-2 rounded-md hover:bg-neutral-50 hover:text-black font-semibold transition-colors"
                      >
                        <Phone className="w-4 h-4 text-emerald-600" />
                        <span>Contact &amp; WhatsApp</span>
                      </Link>
                      <Link
                        href="/about"
                        onClick={() => setHelpOpen(false)}
                        className="flex items-center space-x-2.5 px-3 py-2 rounded-md hover:bg-neutral-50 hover:text-black font-semibold transition-colors"
                      >
                        <Sparkles className="w-4 h-4 text-[#E09F55]" />
                        <span>About Our Heritage</span>
                      </Link>
                      <a
                        href={process.env.NEXT_PUBLIC_ADMIN_URL || "https://adminelavarasi.orgininfo.com"}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setHelpOpen(false)}
                        className="flex items-center justify-between px-3 py-2 rounded-md bg-neutral-100 hover:bg-neutral-900 hover:text-white text-neutral-800 font-bold transition-all mt-1"
                      >
                        <span className="flex items-center space-x-2">
                          <Layers className="w-3.5 h-3.5" />
                          <span>Admin Portal</span>
                        </span>
                        <ExternalLink className="w-3 h-3 opacity-70" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </nav>
          </div>

          {/* LUXURY MEGAMENU PANEL (Clean White E-Commerce Layout) */}
          {megaMenuOpen && (
            <div
              className="absolute left-0 right-0 top-full w-full bg-white text-neutral-900 border-b border-neutral-200 shadow-2xl z-50 animate-in fade-in slide-in-from-top-1 duration-200"
              onMouseEnter={handleMegaMenuEnter}
              onMouseLeave={handleMegaMenuLeave}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {megaMenuTabs.length === 0 || !currentTab ? (
                  <div className="py-12 text-center text-neutral-500 w-full">
                    <Package className="w-10 h-10 mx-auto mb-2 text-neutral-300" />
                    <p className="font-semibold text-sm text-neutral-700">No collections found in catalog</p>
                    <p className="text-xs text-neutral-400 mt-1">Add categories and products in the admin panel to populate this menu.</p>
                  </div>
                ) : (
                  <div className="flex flex-col lg:flex-row items-stretch gap-6 lg:gap-8">
                    {/* Category Tabs on Left */}
                    <div className="w-full lg:w-60 xl:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-neutral-200 pr-0 lg:pr-6 flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 scrollbar-none">
                      {megaMenuTabs.map((tab, idx) => (
                        <button
                          key={tab.id}
                          type="button"
                          onMouseEnter={() => setActiveMegaCategory(idx)}
                          onClick={() => setActiveMegaCategory(idx)}
                          className={`text-left px-3.5 py-2.5 rounded-md text-xs sm:text-[13px] font-medium transition-all flex items-center justify-between group cursor-pointer whitespace-nowrap lg:whitespace-normal ${
                            activeMegaCategory === idx
                              ? 'bg-neutral-100 text-neutral-900 font-bold border-l-4 border-neutral-900'
                              : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                          }`}
                        >
                          <span>{tab.title}</span>
                          <ChevronRight className={`w-3.5 h-3.5 hidden lg:block transition-transform ${activeMegaCategory === idx ? 'translate-x-0.5 text-neutral-900' : 'opacity-25 group-hover:opacity-75'}`} />
                        </button>
                      ))}
                    </div>

                    {/* Right Showcase */}
                    <div className="flex-1 flex flex-col justify-between min-w-0">
                      <div className="flex items-center justify-between pb-3 border-b border-neutral-200 mb-4">
                        <div>
                          <h3 className="font-serif font-bold text-lg sm:text-xl text-neutral-900">
                            {currentTab.title}
                          </h3>
                          {currentTab.description && (
                            <p className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{currentTab.description}</p>
                          )}
                        </div>
                        <Link
                          href={currentTab.viewAllHref}
                          onClick={() => setMegaMenuOpen(false)}
                          className="text-xs sm:text-sm font-semibold text-[#6F0A06] hover:text-black transition-colors flex items-center space-x-1 group/va"
                        >
                          <span>View all</span>
                          <ArrowRight className="w-3.5 h-3.5 group-hover/va:translate-x-1 transition-transform" />
                        </Link>
                      </div>

                      <div
                        ref={carouselTrackRef}
                        className="flex items-start gap-4 overflow-x-auto scroll-smooth scrollbar-none pb-2 pt-1"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                      >
                        {currentTab.items.map((item, i) => (
                          <Link
                            key={i}
                            href={item.href}
                            onClick={() => setMegaMenuOpen(false)}
                            className="group/card flex flex-col shrink-0 w-[135px] sm:w-[150px] md:w-[160px] cursor-pointer"
                          >
                            <div className="relative aspect-[3/4] w-full rounded-md overflow-hidden bg-neutral-100 border border-neutral-200 group-hover/card:border-neutral-900 shadow-xs group-hover/card:shadow-md transition-all duration-300">
                              <Image
                                src={item.image || '/images/pouch_kai_murukku.png'}
                                alt={item.name || 'Elavarasi Traditional Item'}
                                fill
                                sizes="(max-width: 640px) 140px, 175px"
                                unoptimized
                                className="object-cover group-hover/card:scale-105 transition-transform duration-500 ease-out"
                              />
                              {item.badge && (
                                <span className="absolute top-2 left-2 bg-[#6F0A06] text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow-xs">
                                  {item.badge}
                                </span>
                              )}
                              {item.price !== undefined && (
                                <span className="absolute bottom-2 right-2 bg-black/75 backdrop-blur-xs text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                  ₹{item.price}
                                </span>
                              )}
                            </div>
                            <span className="text-xs sm:text-[13px] font-medium text-neutral-800 group-hover/card:text-[#6F0A06] mt-2 transition-colors text-left line-clamp-1">
                              {item.name}
                            </span>
                            {item.subtitle && (
                              <span className="text-[10px] text-neutral-400 text-left line-clamp-1 mt-0.5">
                                {item.subtitle}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>

                      <div className="pt-3 flex items-center justify-between border-t border-neutral-200 mt-3">
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => scrollMegaCarousel('left')}
                            className="w-7 h-7 rounded border border-neutral-300 bg-white hover:bg-neutral-900 text-neutral-700 hover:text-white flex items-center justify-center transition-all cursor-pointer shadow-xs active:scale-95"
                            aria-label="Previous"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => scrollMegaCarousel('right')}
                            className="w-7 h-7 rounded border border-neutral-300 bg-white hover:bg-neutral-900 text-neutral-700 hover:text-white flex items-center justify-center transition-all cursor-pointer shadow-xs active:scale-95"
                            aria-label="Next"
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        <div className="hidden sm:flex items-center space-x-3 text-[11px] text-neutral-500 font-medium">
                          <span>100% Traditional Handcraft</span>
                          <span>&bull;</span>
                          <span>Cold-Pressed Coconut Oil</span>
                          <span>&bull;</span>
                          <span>Fresh Kitchen Batches</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MOBILE MENU DRAWER */}
          {mobileMenuOpen && (
            <div className="lg:hidden bg-white text-neutral-900 border-t border-neutral-200 px-4 py-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200 shadow-2xl max-h-[85vh] overflow-y-auto">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="relative w-full">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search banana chips, kai murukku..."
                  className="w-full bg-neutral-100 text-neutral-900 placeholder:text-neutral-400 text-xs rounded-full pl-4 pr-10 py-2.5 border border-neutral-200 focus:border-neutral-900 focus:outline-none"
                />
                <button
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-600 hover:text-black cursor-pointer"
                >
                  <Search className="w-4 h-4" />
                </button>
              </form>

              {/* Language & Currency in Mobile Drawer */}
              <div className="flex items-center justify-between p-2.5 bg-neutral-50 rounded-xl text-xs font-semibold text-neutral-800 border border-neutral-200">
                <div className="flex items-center space-x-2">
                  <span className="text-neutral-500 font-normal">Language:</span>
                  <button
                    type="button"
                    onClick={() => setSelectedLang(selectedLang === 'Eng' ? 'தமிழ்' : 'Eng')}
                    className="text-[#6F0A06] font-bold underline"
                  >
                    {selectedLang === 'Eng' ? 'English' : 'தமிழ்'}
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-neutral-500 font-normal">Currency:</span>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value)}
                    className="bg-transparent font-bold text-neutral-900 focus:outline-none"
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="AED">AED (د.إ)</option>
                    <option value="SGD">SGD (S$)</option>
                  </select>
                </div>
              </div>

              {/* Navigation Links Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs font-semibold text-neutral-800">
                <Link
                  href="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  Home
                </Link>
                <Link
                  href="/shop"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/shop') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  All Collections
                </Link>
                <Link
                  href="/occasions"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/occasions') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  Occasions &amp; Gifts
                </Link>
                <Link
                  href="/festivals"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/festivals') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  Festival Hampers
                </Link>
                <Link
                  href="/custom-package"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/custom-package') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  Custom Box Builder
                </Link>
                <Link
                  href="/corporate"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/corporate') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  Corporate Gifting
                </Link>
                <Link
                  href="/bulk-orders"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/bulk-orders') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  Bulk Wholesale
                </Link>
                <Link
                  href="/export"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/export') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  Export Division
                </Link>
                <Link
                  href="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/about') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  About Heritage
                </Link>
                <Link
                  href="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`p-2.5 rounded-xl ${isActive('/contact') ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}
                >
                  Help &amp; Contact
                </Link>
              </div>

              {/* Quick Actions */}
              <div className="pt-3 border-t border-neutral-200 flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setIsTrackingOpen(true);
                  }}
                  className="flex items-center space-x-1.5 text-neutral-800 hover:text-black font-bold cursor-pointer"
                >
                  <ArrowLeftRight className="w-3.5 h-3.5 text-neutral-700" />
                  <span>Track Order</span>
                </button>
                <a
                  href="https://wa.me/919443210890"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1.5 text-emerald-700 font-bold"
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span>WhatsApp Us</span>
                </a>
              </div>
            </div>
          )}

        </header>
      </div>

      {/* Document spacer to offset fixed header so page content flows naturally below */}
      <div
        style={{ height: `${spacerHeight}px` }}
        className="w-full transition-[height] duration-150 bg-white"
        aria-hidden="true"
      />
    </>
  );
}
